import { Redirect, RouteComponentProps } from 'react-router-dom';
import { parse } from '@tock/query-string';
import _ from 'lodash';

import { ACTION_CANCEL_RESERVATION } from 'utils/analytics/eventTypeConstantsAmplitude';
import { AmplitudeSendEventProps, withAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { IWithContextProps, core, safeToNumber, withContext } from '@tock/coretypes';
import { ScrollToTopOnMount } from '@tock/hooks';
import { displayRefundInSyncError } from 'components/business/checkout/RefundErrorUtil';
import { lib } from 'types';
import { pastPurchaseToData } from 'utils/analytics/amplitude';
import BaseComponent from '@tock/shared/components/BaseComponent';
import CancelBooking from 'components/business/CancelBooking';
import Link from 'omakase/Link/Link';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface Props {
  business: StoreType['business'];
  purchase: StoreType['purchase'];
  pastPurchase: StoreType['pastPurchase'];
  token: string;
}

interface MatchParams {
  purchaseId: string;
}

type AllProps = DefaultConnectProps &
  Props &
  RouteComponentProps<MatchParams> &
  AmplitudeSendEventProps;

class CancelBookingRoute extends BaseComponent<AllProps & IWithContextProps> {
  componentDidMount() {
    const {
      actions,
      pastPurchase: { pastPurchase },
    } = this.props;
    actions.navigation.set({
      title: 'Cancel reservation',
      hasBack: true,
    });

    const purchase = _.last(_.values(pastPurchase)) as core.PastPurchase;
    if (!this.props.business.business) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.props.actions.business.fetch(purchase.business!.domainName!);
    }
  }

  handleCancelBooking() {
    const {
      app,
      actions,
      history,
      location: { search },
      match: { path },
      pastPurchase: { pastPurchase },
      amplitudeSendEvent,
    } = this.props;
    const { token } = parse(search);
    const purchase = _.last(_.values(pastPurchase));
    const isAuthenticated = !!app.jwtToken;
    const isInProfile = _.includes(path, 'profile');

    const business = purchase?.business;

    if (!business?.id || !purchase) {
      return;
    }

    amplitudeSendEvent(ACTION_CANCEL_RESERVATION, {
      ...pastPurchaseToData(purchase),
    });

    const cancellationPromise = isInProfile
      ? actions.purchase.cancel(purchase.id, business.id).then(() => {
          history.replace(`/profile/reservations/${purchase.id}`);
          actions.toast.showInfo('Reservation canceled');
        })
      : actions.purchase.cancel(purchase.id, business.id, token).then(() => {
          if (isAuthenticated) {
            history.replace(`/${business.domainName}/receipt?purchaseId=${purchase.id}`);
          } else {
            history.replace(`/${business.domainName}/receipt?token=${token}`);
          }
          actions.toast.showInfo('Reservation canceled');
        });

    cancellationPromise.catch((response: { error: lib.ErrorMessageProto }) => {
      if (!response?.error?.errorCode) {
        return this.handleGenericCancellationError();
      }

      const errorCode = safeToNumber(response.error.errorCode);
      if (errorCode === 9013) {
        displayRefundInSyncError(this.props.actions.toast);
      } else {
        this.handleGenericCancellationError();
      }
    });
  }

  handleGenericCancellationError() {
    const { actions } = this.props;
    const cancellationMessageWithLink = (
      <>
        There was a problem cancelling your booking. For further assistance, please contact Tock{' '}
        <Link href="https://www.exploretock.com/help" target="_blank">
          Support
        </Link>
        .
      </>
    );
    actions.toast.showError(cancellationMessageWithLink);
  }

  handleRescheduleBooking() {
    const {
      actions,
      history,
      location: { search },
      pastPurchase: { pastPurchase },
    } = this.props;
    const { token } = parse(search);
    const purchase = _.last(_.values(pastPurchase)) as core.PastPurchase;
    actions.reschedule.setRescheduleInfo(purchase, token);
    history.push(`/${purchase?.business?.domainName}/reschedule/start`);
  }

  render() {
    const {
      business,
      app,
      pastPurchase: { pastPurchase },
      purchase: { requestInProgress },
    } = this.props;
    const purchase = _.last(_.values(pastPurchase)) as core.PastPurchase;
    const isAuthenticated = !!app.jwtToken;

    if (
      (purchase && !purchase.canCancel) ||
      purchase.cancelledOrRefunded ||
      purchase.visitEditToken
    ) {
      return <Redirect to={`/profile/reservations/${purchase.id}`} />;
    }

    return (
      <>
        <ScrollToTopOnMount />
        <CancelBooking
          business={business}
          isAuthenticated={isAuthenticated}
          isSubmitting={requestInProgress}
          onCancelBooking={this.handleCancelBooking}
          onRescheduleBooking={this.handleRescheduleBooking}
          purchase={purchase}
        />
      </>
    );
  }
}

export default connect<AllProps>(withAmplitudeSendEvent(withContext(CancelBookingRoute)), [
  'business',
  'purchase',
  'pastPurchase',
]);
